import { Card, Eyebrow } from '@league/ui';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '../../lib/api';

/**
 * THE PLAYBOOK — what a director is asked to do on a league session, in order.
 *
 * One component, two rails. A director reads it; the commissioner reads the same thing plus the
 * version each list is on. Splitting it into two screens would mean two renderings of one document
 * and, eventually, two documents.
 *
 * IT IS DELIBERATELY READ-ONLY HERE, on both rails. Publishing a new version is a real action with
 * a real consequence — the run of show COPIES the playbook onto each session rather than referencing
 * it, so an edit in March must not rewrite what a director ticked in February — and it is not
 * something to offer casually beside the text. `POST /api/checklists/templates` exists and is
 * commissioner-only; a screen for it is its own piece of work.
 */

interface Template {
  id: string;
  name: string;
  phase: string;
  version: number;
  isActive: boolean;
  items: { id: string; position: number; label: string; detail: string | null; requiresPhoto: boolean }[];
}

/**
 * The order a session actually happens in, rather than however the database returns them.
 *
 * These are the `checklist_phase` enum's own values. Guessed at first, and the screen rendered
 * `RUN_OF_SHOW` at a director — the raw enum, in the one place the platform is supposed to be
 * speaking her language. Read from the schema now.
 */
const PHASE_ORDER = ['setup', 'run_of_show', 'close_down'] as const;

/**
 * Each template already carries a human name — "Before doors open", "During the session" — so the
 * eyebrow says WHEN in the session rather than repeating it. A phase with no label falls back to
 * nothing rather than to its enum value: a missing word is better than a database one.
 */
const PHASE_LABEL: Record<string, string> = {
  setup: 'Before they arrive',
  run_of_show: 'While they play',
  close_down: 'On the way out',
};

export function Playbook({ showVersions = false }: { showVersions?: boolean }) {
  const query = useQuery({
    queryKey: ['checklist-templates'],
    queryFn: () => apiFetch<{ templates: Template[] }>('/api/checklists/templates'),
  });

  if (query.isPending) {
    return <p className="px-6 py-10 text-[14px] text-ink-2">Loading…</p>;
  }

  const templates = (query.data?.templates ?? []).filter((t) => t.isActive);

  const ordered = [...templates].sort(
    (a, b) => PHASE_ORDER.indexOf(a.phase as never) - PHASE_ORDER.indexOf(b.phase as never),
  );

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-6 py-8">
      <div>
        <h1 className="font-display text-3xl leading-tight">The playbook</h1>
        <p className="mt-2 max-w-[60ch] text-[15px] leading-relaxed text-ink-2">
          {/*
            ⚠ THIS SENTENCE POINTED AT A TAB THAT IS NOT THERE — corrected 2026-09-17, owner.

            It read "every session gets its own copy of this on the Run of show tab". The tab was
            taken out of the league workspace in a V1 presentation pass; its route and screen still
            exist, but nothing links to them, so a director following that instruction had nowhere
            to go. Copy that tells somebody to use a thing they cannot reach is worse than saying
            nothing, because she assumes the fault is hers.

            What is left is what the page HONESTLY is today: the list to work from on the night. It
            claims nothing about ticking, because there is currently nowhere to tick.
          */}
          What a league session looks like from start to finish, written by headquarters. Work from
          it on the night — it is the same for every league, and it changes only when headquarters
          publishes a new version.
        </p>
      </div>

      {ordered.length === 0 ? (
        /*
         * A real empty state. The playbook is seeded, so an empty one means somebody has not
         * published it yet rather than that the feature is missing — and saying which is the point.
         */
        <Card className="p-6">
          <p className="text-[15px]">No playbook has been published yet.</p>
          <p className="mt-1.5 text-[14px] text-ink-2">
            Headquarters writes it and publishes a version. Until then there is nothing here to
            work from on the night.
          </p>
        </Card>
      ) : (
        ordered.map((template) => (
          <Card key={template.id} className="p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <Eyebrow>{PHASE_LABEL[template.phase] ?? ''}</Eyebrow>
              {/*
                The version is the commissioner's business, not a director's. She is being asked to
                follow the current list; which numbered revision it happens to be is noise to her,
                and the number would invite "was the old one different" on a session she is running.
              */}
              {showVersions ? (
                <span className="tabular text-[11px] uppercase tracking-[0.06em] text-ink-3">
                  Version {template.version}
                </span>
              ) : null}
            </div>

            <h2 className="mt-1.5 font-display text-xl leading-tight">{template.name}</h2>

            <ol className="mt-4 flex flex-col">
              {[...template.items]
                .sort((a, b) => a.position - b.position)
                .map((item, index) => (
                  <li
                    key={item.id}
                    className="grid grid-cols-[auto_1fr] gap-x-3.5 border-t border-light py-3 last:border-b"
                  >
                    <span className="tabular font-display text-[0.95rem] text-ink-3">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span>
                      <span className="text-[15px]">{item.label}</span>
                      {item.detail ? (
                        <span className="mt-0.5 block text-[13px] leading-snug text-ink-2">
                          {item.detail}
                        </span>
                      ) : null}
                      {item.requiresPhoto ? (
                        <span className="mt-1 block text-[12px] text-ink-2">
                          Wants a photo — one is enough.
                        </span>
                      ) : null}
                    </span>
                  </li>
                ))}
            </ol>
          </Card>
        ))
      )}
    </div>
  );
}
